"use client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Download,
  RefreshCcw,
  Search,
  ShoppingBag,
  Trash2,
  Upload,
} from "lucide-react";
import { Row, Col } from "@/components/Common/Row";
import { PageHeader } from "@/components/Common/PageHeader";
import { EmptyState } from "@/components/Common/EmptyState";
import { CardSkeletonList } from "@/components/Common/CardSkeleton";
import { WorkspaceCard } from "@/components/Common/WorkspaceCard";
import { useAuthedApi } from "@/api/useAuthedApi";
import type { CatalogEntry, CatalogScope } from "@/api/skillsMarketplaceApi";
import { RestError } from "@/api/restClient";
import { PortalScope } from "@/analytics/PortalScope";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ScopeTab = "all" | CatalogScope;

const SCOPE_TABS: { value: ScopeTab; label: string }[] = [
  { value: "all", label: "All" },
  { value: "public", label: "Public" },
  { value: "company", label: "Company" },
  { value: "user", label: "Mine" },
];

const SCOPE_COLOR: Record<CatalogScope, "default" | "primary" | "secondary"> = {
  public: "primary",
  company: "secondary",
  user: "default",
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function SkillsMarketplacePage() {
  const { skillsMarketplace } = useAuthedApi();
  const [entries, setEntries] = useState<CatalogEntry[] | null>(null);
  const [tab, setTab] = useState<ScopeTab>("all");
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Install state
  const [installing, setInstalling] = useState<string | null>(null); // catalog entry id
  const [installError, setInstallError] = useState<string | null>(null);

  // Publish dialog
  const [publishOpen, setPublishOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!skillsMarketplace) return;
    setRefreshing(true);
    setLoadError(null);
    try {
      const res = await skillsMarketplace.list({
        scope: tab === "all" ? undefined : tab,
        search: search.trim() || undefined,
        limit: 200,
      });
      setEntries(res.entries);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshing(false);
    }
  }, [skillsMarketplace, tab, search]);

  // Refresh when tab or search changes (debounced for search).
  useEffect(() => {
    const t = setTimeout(refresh, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [refresh, search, tab]);

  const handleInstall = async (entry: CatalogEntry) => {
    if (!skillsMarketplace) return;
    setInstalling(entry.id);
    setInstallError(null);
    try {
      const res = await skillsMarketplace.install(entry.id, { tier: "user" });
      setNotice(`"${res.name}" installed as a personal skill.`);
      setTimeout(() => setNotice(null), 4000);
    } catch (e) {
      const msg =
        e instanceof RestError ? e.detail : e instanceof Error ? e.message : String(e);
      setInstallError(msg);
    } finally {
      setInstalling(null);
    }
  };

  const handleRemove = async (entry: CatalogEntry) => {
    if (!skillsMarketplace) return;
    try {
      await skillsMarketplace.remove(entry.id);
      setNotice(`"${entry.name}" removed from marketplace.`);
      setTimeout(() => setNotice(null), 4000);
      refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setInstallError(msg);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Skill Marketplace"
        subtitle="Browse and install skills published by your team or the community."
        actions={
          <Row gap={1} data-ph-scope="header">
            <Tooltip title="Refresh">
              <span>
                <IconButton data-ph="refresh" onClick={refresh} disabled={refreshing} aria-label="refresh">
                  <RefreshCcw size={16} />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              data-ph="open_publish"
              variant="contained"
              startIcon={<Upload size={16} />}
              onClick={() => setPublishOpen(true)}
            >
              Publish skill
            </Button>
          </Row>
        }
      />

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search skills…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          },
        }}
      />

      {/* Scope tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as ScopeTab)}
        data-ph-scope="visibility"
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        {SCOPE_TABS.map((t) => (
          <Tab data-ph="select" key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>

      {/* Alerts */}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {installError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInstallError(null)}>
          {installError}
        </Alert>
      )}
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load: {loadError}
        </Alert>
      )}

      {/* List */}
      {refreshing && entries === null ? (
        <CardSkeletonList count={4} />
      ) : entries === null || entries.length === 0 ? (
        <EmptyState
          icon={ShoppingBag}
          title="No skills found"
          description={
            search
              ? `No skills match "${search}".`
              : "Be the first to publish a skill to the marketplace."
          }
          action={
            !search ? (
              <span data-ph-scope="empty" style={{ display: "contents" }}>
                <Button
                  data-ph="open_publish"
                  size="small"
                  startIcon={<Upload size={14} />}
                  onClick={() => setPublishOpen(true)}
                >
                  Publish one
                </Button>
              </span>
            ) : undefined
          }
        />
      ) : (
        <Col gap={1}>
          {entries.map((entry) => (
            <CatalogCard
              key={entry.id}
              entry={entry}
              installing={installing === entry.id}
              onInstall={() => handleInstall(entry)}
              onRemove={() => handleRemove(entry)}
            />
          ))}
        </Col>
      )}

      {/* Publish dialog */}
      {publishOpen && (
        <PublishDialog
          onClose={() => setPublishOpen(false)}
          onPublished={() => {
            setPublishOpen(false);
            refresh();
            setNotice("Skill published to the marketplace.");
            setTimeout(() => setNotice(null), 4000);
          }}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Catalog entry card
// ---------------------------------------------------------------------------

function CatalogCard({
  entry,
  installing,
  onInstall,
  onRemove,
}: {
  entry: CatalogEntry;
  installing: boolean;
  onInstall: () => void;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <WorkspaceCard data-ph-scope="entry">
      <Row align="start" justify="space-between" gap={2}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {/* Header row */}
          <Row gap={1} align="center" sx={{ mb: 0.5, flexWrap: "wrap" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {entry.name}
            </Typography>
            <Chip
              size="small"
              label={entry.scope}
              color={SCOPE_COLOR[entry.scope]}
              variant="outlined"
            />
            {entry.category && entry.category !== "general" && (
              <Chip size="small" label={entry.category} />
            )}
            {entry.tags.map((tag) => (
              <Chip key={tag} size="small" label={tag} variant="outlined" />
            ))}
          </Row>

          {/* Description */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {entry.description}
          </Typography>

          {/* Meta */}
          <Typography variant="caption" color="text.disabled">
            {entry.install_count} install{entry.install_count !== 1 ? "s" : ""} · v
            {entry.version}
          </Typography>

          {/* Expandable body preview */}
          {expanded && (
            <Typography
              variant="caption"
              component="pre"
              sx={(t) => ({
                display: "block",
                mt: 1,
                p: 1,
                bgcolor: "background.default",
                borderRadius: 1,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 220,
                overflowY: "auto",
                fontFamily: "ui-monospace, monospace",
                boxShadow:
                  t.palette.mode === "light"
                    ? "inset 2px 2px 5px rgba(0,0,0,0.08)"
                    : "inset 2px 2px 5px rgba(0,0,0,0.4)",
              })}
            >
              {entry.body}
            </Typography>
          )}

          <Button
            data-ph={expanded ? "collapse" : "expand"}
            size="small"
            sx={{ mt: 0.5, p: 0, minWidth: 0, textTransform: "none" }}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Hide instructions" : "Preview instructions"}
          </Button>
        </Box>

        {/* Actions */}
        <Col gap={0.5} sx={{ flexShrink: 0, alignItems: "flex-end" }}>
          <Tooltip title="Install as personal skill">
            <span>
              {/* Install copies the entry into the user's skills: `import`, as VERBS has no `install`. */}
              <Button
                data-ph="import"
                size="small"
                variant="contained"
                startIcon={installing ? <CircularProgress size={12} /> : <Download size={14} />}
                onClick={onInstall}
                disabled={installing}
                aria-label={`install ${entry.name}`}
              >
                Install
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Remove from marketplace (author only)">
            <IconButton
              data-ph="delete"
              size="small"
              onClick={onRemove}
              aria-label={`remove ${entry.name} from marketplace`}
            >
              <Trash2 size={14} />
            </IconButton>
          </Tooltip>
        </Col>
      </Row>
    </WorkspaceCard>
  );
}

// ---------------------------------------------------------------------------
// Publish dialog
// ---------------------------------------------------------------------------

const CATEGORIES = [
  "general",
  "productivity",
  "data",
  "writing",
  "coding",
  "analysis",
  "communication",
];

function PublishDialog({
  onClose,
  onPublished,
  prefill,
}: {
  onClose: () => void;
  onPublished: () => void;
  prefill?: {
    name: string;
    description: string;
    body: string;
  };
}) {
  const { skillsMarketplace } = useAuthedApi();
  const [name, setName] = useState(prefill?.name ?? "");
  const [description, setDescription] = useState(prefill?.description ?? "");
  const [body, setBody] = useState(prefill?.body ?? "");
  const [tags, setTags] = useState("");
  const [category, setCategory] = useState("general");
  const [scope, setScope] = useState<CatalogScope>("user");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameOk = prefill ? true : /^[a-z][a-z0-9_]{0,63}$/.test(name) && name.length >= 2;
  const descOk = description.length >= 10 && description.length <= 300;
  const bodyOk = body.length >= 50 && body.length <= 15000;

  const parsedTags = useMemo(
    () =>
      tags
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean),
    [tags],
  );

  const onSubmit = async () => {
    if (!skillsMarketplace) return;
    setSaving(true);
    setError(null);
    try {
      await skillsMarketplace.publish({
        name: prefill?.name ?? name,
        description,
        body,
        tags: parsedTags,
        category,
        scope,
      });
      onPublished();
    } catch (e) {
      const msg =
        e instanceof RestError ? e.detail : e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      {/* Shared with the Skills page: the route's area says where it was opened from. */}
      <PortalScope section="publish_dialog">
        <DialogTitle>Publish skill to marketplace</DialogTitle>
        <DialogContent>
          <Col gap={2} sx={{ pt: 1 }}>
            {!prefill && (
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                size="small"
                helperText="Lowercase letters, digits, underscores. 2–64 chars."
                error={!nameOk && name.length > 0}
              />
            )}
            {prefill && (
              <Typography variant="body2" color="text.secondary">
                Publishing <strong>{prefill.name}</strong> to the marketplace.
              </Typography>
            )}
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size="small"
              helperText={`${description.length}/300`}
              error={!descOk && description.length > 0}
            />
            <TextField
              label="Body (instructions)"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              fullWidth
              multiline
              minRows={8}
              maxRows={18}
              helperText={`${body.length}/15000`}
              error={!bodyOk && body.length > 0}
              slotProps={{
                htmlInput: { style: { fontFamily: "ui-monospace, monospace", fontSize: 13 } },
              }}
            />
            <TextField
              label="Tags (comma-separated)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              fullWidth
              size="small"
              helperText="e.g. csv, reporting, finance"
            />
            <Row gap={2}>
              <TextField
                data-ph="open_category"
                select
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                slotProps={{ select: { native: true } }}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </TextField>
              <TextField
                data-ph="open_visibility"
                select
                label="Visibility"
                value={scope}
                onChange={(e) => setScope(e.target.value as CatalogScope)}
                size="small"
                sx={{ flex: 1 }}
                slotProps={{ select: { native: true } }}
              >
                <option value="user">Only me</option>
                <option value="company">My company</option>
                <option value="public">Public (everyone)</option>
              </TextField>
            </Row>
            {error && <Alert severity="error">{error}</Alert>}
          </Col>
        </DialogContent>
        <DialogActions>
          <Button data-ph="cancel" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            data-ph="submit"
            variant="contained"
            onClick={onSubmit}
            disabled={saving || !descOk || !bodyOk || !nameOk}
          >
            {saving ? <CircularProgress size={16} /> : "Publish"}
          </Button>
        </DialogActions>
      </PortalScope>
    </Dialog>
  );
}

// Export dialog for use from Skills page.
export { PublishDialog };
